// Grids & Gantts — the clone dialog.
//
// The reader has ticked some rows and pressed Clone. This asks what a
// clone needs answered — one item or its whole tree, where the copies go,
// which fields to clear or change, and how many copies — shows the tree so
// branches can be left out and new values can be given per work item, and
// then runs the job through the page (see runClone in ./clone.js) while
// saying so, with the site's own logo, so the dialog can be closed and the
// work carries on behind it. When it is done: what was made, what was
// refused, and a button that narrows the structure to the new work.
//
// WHERE THE COPIES GO is asked because a copy that hangs nowhere is a copy
// the page cannot show. The default is the ORIGIN PARENT — the original's
// own parents, joined the same way. CHANGE PARENT searches ALL OF STRIDE for
// the work items whose type may hold the copies by the target project's
// creation tree (see searchParents in ./hierarchy.js) — a clone is made in
// Stride, not in this grid, so the grid's own rows are not the limit (they
// were, until 2026-10-06: another project's modules were "0" because the
// grid had not loaded them). A parent this grid has not loaded is marked:
// the copies are made under it all the same, this grid just won't show
// them. There is no "no parent". THE PARENT IS
// ALWAYS IN THE COPIES' PROJECT: the originals' own project unless a
// different one is chosen, and then that one. Roots ticked from several
// projects cannot share a parent without one of them moving, so Change
// parent then asks for a project above.
//
// FIELDS ARE CLEARED OR CHANGED. The table lists every field a copy would
// carry — the create screens' and the ones carried from the originals
// besides — with two ticks per row. A field ticked "Edit" becomes a column
// of the tree above, one cell per work item, with an editor of the field's
// own kind (see ./CloneValue.jsx) and a "same for all" at the top. The
// admin's short list of fields worth a second look (Page settings →
// Cloning) sits first, as one group that ticks with one click.
//
// Everything here is presentation. The order things are made in, the shape
// of each create, the joins between the copies and the anchors of the
// roots are all in ./clone.js, where the suite can hold them.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FONT } from './theme';
import { CloneBanner, DnaIcon } from '../shared/CloneIcon';
import TypeIcon from '../shared/TypeIcon';
import CloneValue from './CloneValue';
import { anchorPlan, clearableFields, groupClearable, planClone, rankProjects, sameAnchors, schemaOf } from './clone';

const typeIdOf = (it) => String((it && it.fields && it.fields.issuetype && (it.fields.issuetype.id || it.fields.issuetype.name)) || '');
const typeNameOf = (it) => (it && it.fields && it.fields.issuetype && it.fields.issuetype.name) || '';
const projectOf = (it) => (it && it.fields && it.fields.project && it.fields.project.key) || String((it && it.key) || '').split('-')[0];
const summaryOf = (it) => (it && it.fields && it.fields.summary) || '';
const lower = (s) => String(s || '').trim().toLowerCase();
const isBlank = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);
const edgeWords = (edge) => (!edge ? 'the data does not say how' : (edge.kind === 'parent' ? 'by the Parent field' : `by a "${(edge.type && edge.type.name) || 'link'}" link`));

// Every key under a root, through the structure's own tree, once each.
function subtreeKeys(root, kidsOf, out = new Set(), onPath = new Set()) {
  if (onPath.has(root)) return out;
  out.add(root);
  onPath.add(root);
  (kidsOf.get(root) || []).forEach((k) => subtreeKeys(k, kidsOf, out, onPath));
  onPath.delete(root);
  return out;
}

export default function ClonePanel({
  roots, byKey, kidsOf, api, ui, S, workTypes, logo, narrow = false,
  job, onStart, onClose, onFilterClones, metaOf, loadFull = null,
  // The site's field list (names for the fields no create screen lists),
  // the admin's short list of field names, and the page's answers for
  // where a copy may go: the projects, the parents the hierarchy allows,
  // and whether a project offers a work type.
  fieldList = null, attention = null, loadProjects = null, loadParents = null, typeIn = null,
  // Whether this grid has loaded a work item — a parent it has not is marked.
  onGrid = null,
}) {
  const [mode, setMode] = useState('tree');
  const [copies, setCopies] = useState(1);
  const [clear, setClear] = useState(() => new Set());
  const [edit, setEdit] = useState(() => new Set());
  const [edits, setEdits] = useState({});          // key → { fieldId: value }
  const [fillTick, setFillTick] = useState(0);     // bumps when "same for all" writes, so the row editors re-read
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Every key in every root's tree, ticked to begin with.
  const [include, setInclude] = useState(() => {
    const s = new Set();
    roots.forEach((k) => subtreeKeys(k, kidsOf, s));
    return s;
  });
  const plan = useMemo(() => planClone({ roots, byKey, kidsOf, include, mode }), [roots, byKey, kidsOf, include, mode]);

  // ---- where the copies go ----------------------------------------------------------
  const [dest, setDest] = useState({ project: '', placement: 'same', parent: null });
  const rootTypes = useMemo(() => [...new Set(roots.map((k) => typeNameOf(byKey.get(k))).filter(Boolean))], [roots, byKey]);
  const planTypes = useMemo(() => [...new Set(plan.map((n) => typeNameOf(byKey.get(n.key))).filter(Boolean))], [plan, byKey]);
  const ownProjects = useMemo(() => [...new Set(plan.map((n) => projectOf(byKey.get(n.key))).filter(Boolean))], [plan, byKey]);
  const projectChanged = !!dest.project && ownProjects.some((p) => p !== dest.project);
  const placement = projectChanged ? 'new' : dest.placement;
  // The project a new parent must be in — null when the roots span several
  // and no project has been chosen, which is "pick one above" rather than a
  // parent from anywhere.
  const parentProject = projectChanged ? dest.project : (ownProjects.length === 1 ? ownProjects[0] : null);
  // The originals' own parents on the page — what "the same place" means.
  const parentsOf = useMemo(() => {
    const rev = new Map();
    kidsOf.forEach((kids, pk) => kids.forEach((k) => { if (roots.includes(k)) rev.set(k, [...(rev.get(k) || []), pk]); }));
    return (k) => rev.get(k) || [];
  }, [kidsOf, roots]);
  const sameOf = useMemo(() => new Map(roots.map((k) => [k, sameAnchors(k, byKey, parentsOf)])), [roots, byKey, parentsOf]);
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    if (typeof loadProjects !== 'function') return undefined;
    let on = true;
    loadProjects().then((list) => { if (on) setProjects(Array.isArray(list) ? list : []); }).catch(() => { if (on) setProjects([]); });
    return () => { on = false; };
  }, [loadProjects]);
  // THE CANDIDATES ARE SEARCHED, not filtered: what the reader types goes to
  // Zebra Search (key or summary), a moment after they stop typing.
  const [parentQ, setParentQ] = useState('');
  const [parentQDebounced, setParentQDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setParentQDebounced(parentQ.trim()), 280);
    return () => clearTimeout(t);
  }, [parentQ]);
  const [cands, setCands] = useState({ key: '', list: null, error: null, more: false, types: [] });
  const candKey = `${rootTypes.join('|')}@${parentProject || ''}?${parentQDebounced}`;
  // ONE SEARCH PER QUESTION, and its answer is kept. The effect runs when
  // the question (candKey) changes and NEVER on its own state: it used to
  // list `cands.key` among its dependencies, so marking the search started
  // re-ran the effect, whose clean-up threw away the answer in flight — the
  // box sat at "searching…" for good (live, 2026-10-06). A newer question
  // still discards an older answer, which is what the clean-up is for.
  const rootTypesRef = useRef(rootTypes);
  rootTypesRef.current = rootTypes;
  useEffect(() => {
    if (placement !== 'new' || !parentProject || typeof loadParents !== 'function') return undefined;
    let on = true;
    setCands((c) => ({ ...c, key: candKey, list: null, error: null }));
    Promise.resolve(loadParents(rootTypesRef.current, parentProject, parentQDebounced))
      .then((r) => {
        if (!on) return;
        // An older host answers a plain list (the grid's own rows).
        const got = Array.isArray(r) ? { list: r, more: false, types: null } : (r || {});
        setCands({ key: candKey, list: got.list || [], error: null, more: !!got.more, types: got.types || null });
      })
      .catch((e) => { if (on) setCands({ key: candKey, list: [], error: (e && e.message) || 'the hierarchy could not be read', more: false, types: [] }); });
    return () => { on = false; };
    // candKey carries the root types, the project and the text.
  }, [placement, loadParents, candKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownCands = cands.list || [];
  const isOnGrid = (key) => (typeof onGrid === 'function' ? onGrid(key) : true);
  const holderWords = (cands.types && cands.types.length) ? cands.types.join(' or ') : 'work';
  // A copy in another project needs every work type in the tree to exist there.
  const [typeGaps, setTypeGaps] = useState({ project: '', missing: [], checking: false });
  useEffect(() => {
    if (!projectChanged || typeof typeIn !== 'function') return undefined;
    let on = true;
    setTypeGaps({ project: dest.project, missing: [], checking: true });
    Promise.all(planTypes.map((t) => typeIn(dest.project, t).then((hit) => (hit ? null : t)).catch(() => t)))
      .then((gaps) => { if (on) setTypeGaps({ project: dest.project, missing: gaps.filter(Boolean), checking: false }); });
    return () => { on = false; };
  }, [projectChanged, dest.project, planTypes, typeIn]);
  const typeMissing = projectChanged && typeGaps.project === dest.project ? typeGaps.missing : [];
  const typeChecking = projectChanged && (typeGaps.project !== dest.project || typeGaps.checking);

  // ---- the create screens, and what the copies carry ------------------------------------
  // One screen per (project, work type) in the plan — in the destination
  // project when the copies change project.
  const [metas, setMetas] = useState([]);
  const [metaErr, setMetaErr] = useState(null);
  const pairs = useMemo(() => {
    const seen = new Map();
    plan.forEach((n) => {
      const it = byKey.get(n.key);
      const projectKey = projectChanged ? dest.project : projectOf(it);
      const k = `${projectKey}:${typeIdOf(it)}`;
      if (!seen.has(k)) seen.set(k, { projectKey, typeId: (it.fields.issuetype && it.fields.issuetype.id) || '', typeName: typeNameOf(it) });
    });
    return [...seen.values()];
  }, [plan, byKey, projectChanged, dest.project]);
  useEffect(() => {
    let on = true;
    setMetaErr(null);
    Promise.all(pairs.map((p) => metaOf(p.projectKey, p.typeId, p.typeName).then((meta) => ({ ...p, meta })).catch(() => null)))
      .then((list) => { if (on) setMetas(list.filter(Boolean)); })
      .catch((e) => { if (on) setMetaErr((e && e.message) || 'the create screens could not be read'); });
    return () => { on = false; };
  }, [pairs, metaOf]);
  // The originals in full — the page's rows carry only the columns' fields,
  // and both "holds a value on" and "what a copy carries" are counted over
  // everything.
  const [full, setFull] = useState(() => new Map());
  const planKeys = useMemo(() => plan.map((n) => n.key).join(','), [plan]);
  useEffect(() => {
    if (typeof loadFull !== 'function' || !planKeys) return undefined;
    let on = true;
    loadFull(planKeys.split(',')).then((m) => { if (on && m) setFull(m); }).catch(() => {});
    return () => { on = false; };
  }, [loadFull, planKeys]);
  const planItems = useMemo(() => plan.map((n) => full.get(n.key) || byKey.get(n.key)).filter(Boolean), [plan, full, byKey]);
  const clearable = useMemo(() => clearableFields(metas, { items: planItems, fieldList: fieldList || [], also: attention || [] }), [metas, planItems, fieldList, attention]);
  // A SEARCH OVER THE FIELDS. A site has a hundred and more of them and the
  // one wanted was a scroll-and-squint away; typing part of its name narrows
  // the table, and the ticks above a narrowed table take only what is shown.
  const [fieldQ, setFieldQ] = useState('');
  const q = fieldQ.trim().toLowerCase();
  const shownFields = useMemo(() => (q ? clearable.filter((fl) => String(fl.name || '').toLowerCase().includes(q) || String(fl.fieldId || '').toLowerCase().includes(q)) : clearable), [clearable, q]);
  const groups = useMemo(() => groupClearable(shownFields, attention || []), [shownFields, attention]);
  const grouped = groups.important.length > 0;
  // THE TABLE IS AS TALL AS THE READER WANTS IT. Dragged by the grip under
  // it, and remembered for the next clone on this browser.
  const [fieldsH, setFieldsH] = useState(() => {
    try { return Number(localStorage.getItem('grids.clone.fieldsH')) || 260; } catch { return 260; }
  });
  const fieldsDrag = useRef(null);
  useEffect(() => {
    const move = (e) => {
      const d = fieldsDrag.current;
      if (!d) return;
      const max = Math.max(200, Math.round(window.innerHeight * 0.85));
      d.h = Math.max(120, Math.min(max, Math.round(d.h0 + (e.clientY - d.y0))));
      setFieldsH(d.h);
    };
    const up = () => {
      const d = fieldsDrag.current;
      if (!d) return;
      fieldsDrag.current = null;
      document.body.style.cursor = '';
      if (d.h) { try { localStorage.setItem('grids.clone.fieldsH', String(d.h)); } catch { /* storage may be blocked */ } }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, []);
  const byField = useMemo(() => new Map(clearable.map((f) => [f.fieldId, f])), [clearable]);
  // How many of the work items in the plan hold a value in each field.
  const carries = useMemo(() => {
    const m = new Map();
    planItems.forEach((it) => {
      Object.keys(it.fields || {}).forEach((id) => { if (!isBlank(it.fields[id])) m.set(id, (m.get(id) || 0) + 1); });
    });
    return m;
  }, [planItems]);
  // The field as THIS work item's create screen defines it (its allowed
  // values, its kind), or as the list knows it when no screen lists it.
  const defFor = (it, fieldId) => {
    const projectKey = projectChanged ? dest.project : projectOf(it);
    const hit = metas.find((m) => m.projectKey === projectKey && String(m.typeId) === String((it.fields.issuetype && it.fields.issuetype.id) || ''));
    const def = hit && hit.meta && hit.meta[fieldId];
    // schemaOf fills in what a list holds — the screen leaves it out, and a
    // multi-select without it was offered as a text box.
    if (def) return { name: def.name, schema: schemaOf(def), allowedValues: def.allowedValues };
    const f = byField.get(fieldId);
    return f ? { name: f.name, schema: f.schema, allowedValues: f.allowedValues } : { name: fieldId, schema: { type: 'string' } };
  };

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  // ---- ticks ----------------------------------------------------------------------
  // Clear and Edit are one or the other per field: a field with a new value
  // is not also emptied.
  const flip = (set, id, on) => { const n = new Set(set); if (on) n.add(id); else n.delete(id); return n; };
  const setClearOn = (ids, on) => {
    setClear((prev) => { let n = prev; ids.forEach((id) => { if (!(byField.get(id) || {}).noClear) n = flip(n, id, on); }); return n; });
    if (on) setEdit((prev) => { let n = prev; ids.forEach((id) => { if (!(byField.get(id) || {}).noClear) n = flip(n, id, false); }); return n; });
  };
  const setEditOn = (ids, on) => {
    setEdit((prev) => { let n = prev; ids.forEach((id) => { n = flip(n, id, on); }); return n; });
    if (on) setClear((prev) => { let n = prev; ids.forEach((id) => { n = flip(n, id, false); }); return n; });
  };
  const editList = useMemo(() => clearable.filter((f) => edit.has(f.fieldId)), [clearable, edit]);
  const setValue = (key, fieldId, v) => setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [fieldId]: v } }));
  const setAll = (fieldId, v) => {
    setEdits((prev) => { const next = { ...prev }; plan.forEach((n) => { next[n.key] = { ...(next[n.key] || {}), [fieldId]: v }; }); return next; });
    setFillTick((t) => t + 1);
  };
  const valueOf = (key, fieldId) => (edits[key] || {})[fieldId];

  const toggleInclude = (key, on) => {
    setInclude((prev) => {
      const next = new Set(prev);
      const branch = subtreeKeys(key, kidsOf);
      branch.forEach((k) => { if (on) next.add(k); else next.delete(k); });
      return next;
    });
  };
  const toggleShut = (key) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  // ---- the tree, collapsible, with the type icons — and a cell per edited field ------
  const gridCols = editList.length ? `minmax(${narrow ? 240 : 380}px, 1fr) repeat(${editList.length}, minmax(190px, 230px))` : '1fr';
  const cell = { padding: '2px 6px', borderLeft: `1px solid ${ui.border}`, display: 'flex', alignItems: 'center', minWidth: 0 };
  const node = (key, level, onPath) => {
    const it = byKey.get(key);
    if (!it || onPath.has(key)) return null;
    const kids = (kidsOf.get(key) || []).filter((k) => byKey.has(k));
    const shut = collapsed.has(key);
    const on = include.has(key);
    const fullIt = full.get(key) || it;
    return (
      <div key={key}>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', borderTop: level || editList.length ? `1px solid ${ui.border}22` : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', paddingLeft: 4 + level * 18, opacity: on ? 1 : 0.5, minWidth: 0 }}>
            <button onClick={() => kids.length && toggleShut(key)} aria-label={kids.length ? (shut ? 'Expand' : 'Collapse') : undefined}
              style={{ background: 'none', border: 'none', width: 16, padding: 0, cursor: kids.length ? 'pointer' : 'default', color: ui.muted, fontFamily: FONT, fontSize: 12, flex: '0 0 16px' }}>
              {kids.length ? (shut ? '▸' : '▾') : ''}
            </button>
            {mode === 'tree' || level === 0 ? (
              <input type="checkbox" checked={on} disabled={level === 0 && roots.length === 1} onChange={(e) => toggleInclude(key, e.target.checked)}
                title={level === 0 ? 'The root of this clone' : (on ? 'Leave this branch out' : 'Put this branch back')}
                style={{ margin: 0, accentColor: ui.primary }} />
            ) : null}
            <TypeIcon issue={it} types={workTypes} ui={ui} size={14} />
            <span style={{ color: ui.blue, fontSize: 12, fontWeight: 700, flex: '0 0 auto' }}>{key}</span>
            <span style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: on ? 'none' : 'line-through' }}>{summaryOf(it)}</span>
            {kids.length && shut ? <span style={S.tiny}>· {kids.length}</span> : null}
          </div>
          {editList.map((f) => (
            <div key={f.fieldId} style={cell} title={on ? `${f.name} on the copy of ${key} — blank keeps "${textOf(fullIt.fields && fullIt.fields[f.fieldId])}"` : 'Not cloned'}>
              {on ? (
                <CloneValue key={`${key}:${f.fieldId}:${fillTick}`} fieldId={f.fieldId} def={defFor(it, f.fieldId)} value={valueOf(key, f.fieldId)}
                  onChange={(v) => setValue(key, f.fieldId, v)} api={api} ui={ui} S={S}
                  placeholder={textOf(fullIt.fields && fullIt.fields[f.fieldId]) ? `keep "${textOf(fullIt.fields[f.fieldId])}"` : 'keep (empty)'} />
              ) : <span style={{ color: ui.faint, fontSize: 12 }}>—</span>}
            </div>
          ))}
        </div>
        {mode === 'tree' && !shut ? (() => { onPath.add(key); const rows = kids.map((k) => node(k, level + 1, onPath)); onPath.delete(key); return rows; })() : null}
      </div>
    );
  };

  const counted = plan.length;
  const total = counted * Math.max(1, Number(copies) || 1);
  const running = job && job.status === 'running';
  const finished = job && job.status === 'done';
  const btn = { ...S.exportBtn, padding: '6px 12px', fontSize: 12.5 };
  const big = (on) => ({ ...S.pill(on), padding: '10px 14px', fontSize: 13, textAlign: 'left', flex: '1 1 240px', lineHeight: 1.35 });
  const needParent = placement === 'new' && !dest.parent;
  const mixed = placement === 'new' && !parentProject;
  const blocked = !counted || !metas.length || needParent || typeChecking || typeMissing.length > 0;
  const why = !counted ? 'nothing to clone' : (!metas.length ? 'reading the create screens' : (needParent ? (mixed ? `the ticked work items are in ${ownProjects.length} projects — pick one project above` : (projectChanged ? 'a new project needs a parent there — pick one' : `pick the new parent in ${parentProject}`)) : (typeChecking ? 'checking the work types' : (typeMissing.length ? `${dest.project} lacks ${typeMissing.join(', ')}` : ''))));

  const start = () => {
    const anchorsOf = (rootKey) => {
      if (placement === 'new') {
        if (!dest.parent) return [];
        const edge = dest.parent.edgeByType[lower(typeNameOf(byKey.get(rootKey)))];
        return edge ? [{ key: dest.parent.key, edge }] : [];
      }
      return sameOf.get(rootKey) || [];
    };
    const prunedEdits = {};
    plan.forEach((n) => {
      const e = edits[n.key] || {};
      const keep = {};
      editList.forEach((f) => { if (!isBlank(e[f.fieldId])) keep[f.fieldId] = e[f.fieldId]; });
      if (Object.keys(keep).length) prunedEdits[n.key] = keep;
    });
    onStart({
      plan: anchorPlan(plan, anchorsOf), clear: [...clear], copies: Math.max(1, Number(copies) || 1),
      edits: prunedEdits, project: projectChanged ? dest.project : null, placement,
      parentKey: placement === 'new' && dest.parent ? dest.parent.key : null,
    });
  };

  // ---- one row of the fields table -------------------------------------------------
  const fieldRow = (f) => {
    const isClear = clear.has(f.fieldId);
    const isEdit = edit.has(f.fieldId);
    return (
      <tr key={f.fieldId} style={{ borderTop: `1px solid ${ui.border}`, background: isClear ? `${ui.red}10` : (isEdit ? `${ui.primary}12` : 'transparent') }}>
        <td style={{ padding: '4px 8px' }}>
          <input type="checkbox" checked={isClear} disabled={!!f.noClear} onChange={(e) => setClearOn([f.fieldId], e.target.checked)}
            title={f.noClear ? 'A create needs a summary — change it instead' : (isClear ? 'Carry it after all' : 'Leave this field empty on every copy')}
            style={{ margin: 0, accentColor: ui.red, cursor: f.noClear ? 'default' : 'pointer' }} />
        </td>
        <td style={{ padding: '4px 8px' }}>
          <input type="checkbox" checked={isEdit} onChange={(e) => setEditOn([f.fieldId], e.target.checked)}
            title={isEdit ? 'Stop changing this field' : 'Give this field a new value, per work item, in the tree above'}
            style={{ margin: 0, accentColor: ui.primary, cursor: 'pointer' }} />
        </td>
        <td style={{ padding: '4px 8px', fontWeight: isClear || isEdit ? 700 : 400, textDecoration: isClear ? 'line-through' : 'none' }}>{f.name}</td>
        <td style={{ padding: '4px 8px', color: ui.muted, fontSize: 11.5 }}>
          {f.onScreen ? f.types.join(', ') : <em title="No create screen lists it, but the originals hold it and a copy carries every field they hold">carried from the original — on no create screen</em>}
        </td>
        <td style={{ padding: '4px 8px', textAlign: 'right', color: carries.get(f.fieldId) ? ui.fg : ui.faint, whiteSpace: 'nowrap' }}>
          {carries.get(f.fieldId) || 0} of {counted}
        </td>
      </tr>
    );
  };
  // A group's heading, with the two ticks that take the whole group.
  const groupRow = (label, list, hint) => {
    const ids = list.filter((f) => !f.noClear).map((f) => f.fieldId);
    const allIds = list.map((f) => f.fieldId);
    const allClear = ids.length > 0 && ids.every((id) => clear.has(id));
    const allEdit = allIds.length > 0 && allIds.every((id) => edit.has(id));
    return (
      <tr key={`g:${label}`} style={{ background: ui.surface, borderTop: `1px solid ${ui.border}` }}>
        <td style={{ padding: '5px 8px' }}>
          <input type="checkbox" checked={allClear} onChange={(e) => setClearOn(ids, e.target.checked)} title={`Clear every field in "${label}", or none`} style={{ margin: 0, accentColor: ui.red, cursor: 'pointer' }} />
        </td>
        <td style={{ padding: '5px 8px' }}>
          <input type="checkbox" checked={allEdit} onChange={(e) => setEditOn(allIds, e.target.checked)} title={`Edit every field in "${label}", or none`} style={{ margin: 0, accentColor: ui.primary, cursor: 'pointer' }} />
        </td>
        <td colSpan={3} style={{ padding: '5px 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted }}>
          {label}{hint ? <span style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0, marginLeft: 8 }}>{hint}</span> : null}
        </td>
      </tr>
    );
  };

  const body = running || finished ? (
    <div style={{ padding: '6px 2px' }}>
      {running ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ position: 'relative', width: 200, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 12 }}>
              {logo ? <img src={logo} alt="" style={{ maxWidth: 190, maxHeight: 54, objectFit: 'contain' }} /> : <CloneBanner height={54} />}
              <span style={{ position: 'absolute', top: 0, bottom: 0, width: '30%', background: `linear-gradient(90deg, transparent, ${ui.card}cc, transparent)`, animation: 'wip-sweep 1.4s linear infinite' }} />
            </div>
            <CloneBanner height={56} title="Cloning" />
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>Cloning in progress</div>
              <div style={{ ...S.tiny, marginTop: 3 }}>
                You are welcome to close this pop-up while Zebra Stride does your cloning — the toolbar keeps count, and the report waits there.
              </div>
            </div>
          </div>
          <div style={{ width: '100%', height: 8, borderRadius: 8, background: ui.surface, border: `1px solid ${ui.border}`, overflow: 'hidden' }}>
            <div style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 5}%`, height: '100%', background: ui.blue, transition: 'width .3s' }} />
          </div>
          <div style={{ ...S.tiny, marginTop: 6 }}>{job.done} of {job.total} · {job.createdCount} work item(s) made so far</div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <CloneBanner height={48} title="Cloned" />
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{job.created.length} work item(s) made{job.errors.length ? `, ${job.errors.length} problem(s)` : ''}</div>
              <div style={S.tiny}>{job.copies} cop{job.copies === 1 ? 'y' : 'ies'} of {job.planSize} work item(s){job.project ? ` in ${job.project}` : ''}{job.parentKey ? ` under ${job.parentKey}` : ''}</div>
            </div>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
              {job.created.length ? <button onClick={() => onFilterClones(job.created.map((c) => c.key))} style={{ ...S.pill(true), padding: '6px 12px' }}>Filter my clones</button> : null}
              <button onClick={onClose} style={btn}>Close</button>
            </span>
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${ui.border}`, borderRadius: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
              <thead><tr style={{ background: ui.surface }}>
                <th style={{ textAlign: 'left', padding: '5px 8px', fontSize: 11 }}>Copy</th>
                <th style={{ textAlign: 'left', padding: '5px 8px', fontSize: 11 }}>From</th>
                <th style={{ textAlign: 'left', padding: '5px 8px', fontSize: 11 }}>Made</th>
                <th style={{ textAlign: 'left', padding: '5px 8px', fontSize: 11 }}>Summary</th>
              </tr></thead>
              <tbody>
                {job.created.map((c) => (
                  <tr key={`${c.copy}-${c.key}`} style={{ borderTop: `1px solid ${ui.border}` }}>
                    <td style={{ padding: '4px 8px' }}>{c.copy}</td>
                    <td style={{ padding: '4px 8px', color: ui.muted }}>{c.from}</td>
                    <td style={{ padding: '4px 8px', color: ui.blue, fontWeight: 700 }}>{c.key}</td>
                    <td style={{ padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>{summaryOf(byKey.get(c.from))}</td>
                  </tr>
                ))}
                {job.errors.map((e, i) => (
                  <tr key={`e${i}`} style={{ borderTop: `1px solid ${ui.border}`, color: ui.red }}>
                    <td style={{ padding: '4px 8px' }}>{e.copy}</td>
                    <td style={{ padding: '4px 8px' }}>{e.from}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 700 }}>⚠</td>
                    <td style={{ padding: '4px 8px' }}>{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  ) : (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => setMode('tree')} style={big(mode === 'tree')}>
          <div>Clone the full architectural tree</div>
          <div style={{ ...S.tiny, fontWeight: 400 }}>The work item and everything under it, joined the way the originals are. Untick a branch to leave it out.</div>
        </button>
        <button onClick={() => setMode('single')} style={big(mode === 'single')}>
          <div>Single work item clone</div>
          <div style={{ ...S.tiny, fontWeight: 400 }}>Just the work item{roots.length > 1 ? 's' : ''} ticked, nothing under {roots.length > 1 ? 'them' : 'it'}.</div>
        </button>
      </div>

      {/* WHAT WILL BE CLONED — the tree, and a column per field being changed. */}
      <div style={{ ...S.label, marginBottom: 4 }}>
        {mode === 'tree' ? 'What will be cloned' : 'The work item' + (roots.length > 1 ? 's' : '')}
        {editList.length ? <span style={{ textTransform: 'none', fontWeight: 400, color: ui.muted }}> — a new value per work item; a blank keeps the original&rsquo;s</span> : null}
      </div>
      <div style={{ border: `1px solid ${ui.border}`, borderRadius: 8, padding: '4px 8px', maxHeight: 340, overflow: 'auto', marginBottom: 12 }}>
        {editList.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', position: 'sticky', top: 0, background: ui.card, zIndex: 2, borderBottom: `1px solid ${ui.border}` }}>
            <div style={{ ...S.tiny, fontWeight: 700, padding: '4px 0' }}>Work item</div>
            {editList.map((f) => (
              <div key={f.fieldId} style={{ ...cell, flexDirection: 'column', alignItems: 'stretch', gap: 2, padding: '4px 6px' }}>
                <div style={{ ...S.tiny, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                  <button onClick={() => setEditOn([f.fieldId], false)} title="Stop changing this field" style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: ui.muted, padding: 0, fontFamily: FONT, lineHeight: 1 }}>×</button>
                </div>
                <div title="Same for all — writes this value into every row below; a row can still be changed after">
                  <CloneValue fieldId={f.fieldId} def={defFor(planItems[0] || byKey.get(roots[0]), f.fieldId)} value={undefined}
                    onChange={(v) => setAll(f.fieldId, v)} api={api} ui={ui} S={S} placeholder="same for all…" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {roots.map((k) => node(k, 0, new Set()))}
      </div>

      {/* WHERE THE COPIES GO. */}
      <div style={{ ...S.label, marginBottom: 4 }}>Where the copies go</div>
      <div style={{ border: `1px solid ${ui.border}`, borderRadius: 8, padding: '8px 10px', marginBottom: 12, display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...S.tiny, fontWeight: 700, minWidth: 56 }}>Project</span>
          <ProjectPick value={dest.project} projects={projects} ui={ui} S={S} changed={projectChanged}
            ownLabel={`Same as the original${ownProjects.length > 1 ? 's' : ''} (${ownProjects.join(', ')})`}
            onChange={(k) => { setParentQ(''); setDest((d) => ({ ...d, project: k, parent: null, placement: k ? 'new' : d.placement })); }} />
          {projects === null && typeof loadProjects === 'function' ? <span style={S.tiny}>reading the projects…</span> : null}
          {projectChanged ? (
            typeChecking ? <span style={S.tiny}>checking that {dest.project} offers {planTypes.join(', ')}…</span>
              : (typeMissing.length
                ? <span style={{ ...S.tiny, color: ui.red, fontWeight: 700 }}>⚠ {dest.project} has no work type {typeMissing.map((t) => `"${t}"`).join(', ')} — every type in the tree must exist there</span>
                : <span style={{ ...S.tiny, color: ui.amber, fontWeight: 700 }}>The copies are made in {dest.project} — so they need a parent there, picked below</span>)
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...S.tiny, fontWeight: 700, minWidth: 56 }}>Parent</span>
          {[
            ['same', 'Origin parent', projectChanged ? 'Not in another project' : roots.map((k) => {
              const a = sameOf.get(k) || [];
              return a.length ? `${k}: under ${a.map((x) => `${x.key} (${edgeWords(x.edge)})`).join(', ')}` : `${k}: has no parent in this structure — the copy has none either`;
            }).join('\n')],
            ['new', 'Change parent', `Search every work item in ${parentProject || 'the copies’ project'} whose type may hold ${rootTypes.join(' and ')} work by the architectural hierarchy — across Stride, not only this grid`],
          ].map(([id, label, hint]) => (
            <label key={id} title={hint} style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center', cursor: projectChanged && id !== 'new' ? 'not-allowed' : 'pointer', opacity: projectChanged && id !== 'new' ? 0.5 : 1, color: placement === id ? ui.fg : ui.muted, fontWeight: placement === id ? 700 : 400 }}>
              <input type="radio" name="clone-placement" checked={placement === id} disabled={projectChanged && id !== 'new'}
                onChange={() => setDest((d) => ({ ...d, placement: id }))} style={{ margin: 0, accentColor: ui.primary }} />
              {label}
            </label>
          ))}
        </div>
        {placement === 'same' && !projectChanged ? (
          <div style={{ ...S.tiny, whiteSpace: 'pre-line' }}>
            {roots.map((k) => {
              const a = sameOf.get(k) || [];
              return a.length ? `${k} → under ${a.map((x) => `${x.key} (${edgeWords(x.edge)})`).join(', ')}` : `${k} → has no parent in this structure, so the copy has none either`;
            }).join('\n')}
          </div>
        ) : null}
        {placement === 'new' && mixed ? (
          <div style={{ ...S.tiny, color: ui.amber }}>
            The ticked work items are in {ownProjects.length} projects ({ownProjects.join(', ')}); a parent is always in the copies&rsquo; own project,
            so one parent cannot hold them all. Pick a project above to move them together, or clone one project&rsquo;s work items at a time.
          </div>
        ) : null}
        {placement === 'new' && !mixed ? (
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              <input value={parentQ} onChange={(e) => setParentQ(e.target.value)} placeholder="Find the new parent by key or summary…"
                style={{ ...S.input, padding: '5px 9px', fontSize: 12.5, flex: '1 1 260px', maxWidth: 420 }} />
              <span style={S.tiny}>
                {cands.list === null ? `searching ${parentProject}…`
                  : (cands.error ? <span style={{ color: ui.red }}>⚠ {cands.error}</span>
                    : `${cands.list.length.toLocaleString()}${cands.more ? '+' : ''} ${holderWords} work item(s) in ${parentProject}${parentQDebounced ? ` matching “${parentQDebounced}”` : ''} may hold ${rootTypes.join(' and ')} work — by its architectural hierarchy, across Stride`)}
              </span>
            </div>
            {dest.parent ? (
              <div style={{ ...S.tiny, marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: ui.fg }}>Under</span>
                <TypeIcon issue={dest.parent.item} types={workTypes} ui={ui} size={14} />
                <span style={{ color: ui.blue, fontWeight: 700 }}>{dest.parent.key}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{summaryOf(dest.parent.item)}</span>
                <span>— {rootTypes.map((t) => `${t} ${edgeWords(dest.parent.edgeByType[lower(t)])}`).join('; ')}</span>
                <button onClick={() => setDest((d) => ({ ...d, parent: null }))} style={{ ...S.exportBtn, padding: '1px 8px', fontSize: 11 }}>change</button>
                {!isOnGrid(dest.parent.key) ? (
                  <span style={{ flex: '1 1 100%', color: ui.amber }}>
                    {dest.parent.key} is not on this grid — the copies are made under it all the same, but this grid will not show them (the report lists their keys).
                  </span>
                ) : null}
              </div>
            ) : null}
            {cands.list && cands.list.length ? (
              <div style={{ border: `1px solid ${ui.border}`, borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                {shownCands.map((c) => (
                  <div key={c.key} onClick={() => setDest((d) => ({ ...d, parent: c }))}
                    style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 8px', cursor: 'pointer', fontSize: 12.5, background: dest.parent && dest.parent.key === c.key ? `${ui.primary}14` : 'transparent', borderTop: `1px solid ${ui.border}22` }}>
                    <TypeIcon issue={c.item} types={workTypes} ui={ui} size={14} />
                    <span style={{ color: ui.blue, fontWeight: 700, flex: '0 0 auto' }}>{c.key}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summaryOf(c.item)}</span>
                    <span style={{ ...S.tiny, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                      {c.item && c.item.fields && c.item.fields.status && c.item.fields.status.name ? `${c.item.fields.status.name} · ` : ''}{c.typeName}{isOnGrid(c.key) ? '' : ' · not on this grid'}
                    </span>
                  </div>
                ))}
                {cands.more ? <div style={{ ...S.tiny, padding: '3px 8px' }}>… more — type part of a key or summary to narrow</div> : null}
              </div>
            ) : (cands.list && !cands.list.length && !cands.error ? (
              <div style={{ ...S.tiny, color: ui.amber }}>
                {parentQDebounced
                  ? `No ${holderWords} work item in ${parentProject} matches “${parentQDebounced}”.${/^[A-Za-z][A-Za-z0-9_]*-\d+$/.test(parentQDebounced) && !parentQDebounced.toUpperCase().startsWith(`${parentProject}-`) ? ` Keys in ${parentProject} start with ${parentProject}-.` : ''}`
                  : (cands.types && !cands.types.length
                    ? `${parentProject}’s architectural hierarchy has no work type that may hold ${rootTypes.join(' and ')} work${projectChanged ? ' — pick another project' : ''}.`
                    : `${parentProject} has no ${holderWords} work item yet to hold ${rootTypes.join(' and ')} work${projectChanged ? ' — create one first, or pick another project' : ''}.`)}
              </div>
            ) : null)}
          </div>
        ) : null}
      </div>

      {/* FIELDS TO CHANGE — a table, not a cloud of checkboxes: each field on
          its own row, Clear or Edit, the work types whose create screen
          carries it (or that it is carried from the originals), and how many
          of the work items about to be cloned actually hold a value in it —
          the number that decides whether clearing it matters. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={S.label}>Fields to change during cloning <span style={{ textTransform: 'none', fontWeight: 400, color: ui.muted }}>— everything else is carried across</span></div>
        {clearable.length ? (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="search" value={fieldQ} onChange={(e) => setFieldQ(e.target.value)} placeholder="Search the fields…" aria-label="Search the fields"
              // Escape empties the search first; only an empty one closes the dialog.
              onKeyDown={(e) => { if (e.key === 'Escape' && fieldQ) { e.preventDefault(); e.stopPropagation(); setFieldQ(''); } }}
              style={{ ...S.input, padding: '4px 9px', fontSize: 12.5, width: 220 }} />
            {q ? <span style={S.tiny}>{shownFields.length} of {clearable.length}</span> : null}
          </span>
        ) : null}
      </div>
      {metaErr ? <div style={{ ...S.tiny, color: ui.red }}>⚠ {metaErr}</div> : null}
      {!metas.length && !metaErr ? <div style={S.tiny}>reading the create screens…</div> : null}
      {clearable.length ? (
        <div style={{ marginBottom: 12 }}>
        <div style={{ border: `1px solid ${ui.border}`, borderRadius: '8px 8px 0 0', height: fieldsH, overflowY: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: ui.surface, position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '5px 8px', fontSize: 11, textAlign: 'left', width: 56 }}>
                  <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }} title="Clear every field, or none — the copies carry nothing but the summary">
                    <input type="checkbox" checked={shownFields.some((f) => !f.noClear) && shownFields.every((f) => f.noClear || clear.has(f.fieldId))}
                      onChange={(e) => setClearOn(shownFields.map((f) => f.fieldId), e.target.checked)} style={{ margin: 0, accentColor: ui.red }} />
                    Clear
                  </label>
                </th>
                <th style={{ padding: '5px 8px', fontSize: 11, textAlign: 'left', width: 52 }}>
                  <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }} title="Change every field, or none">
                    <input type="checkbox" checked={shownFields.length > 0 && shownFields.every((f) => edit.has(f.fieldId))}
                      onChange={(e) => setEditOn(shownFields.map((f) => f.fieldId), e.target.checked)} style={{ margin: 0, accentColor: ui.primary }} />
                    Edit
                  </label>
                </th>
                <th style={{ padding: '5px 8px', fontSize: 11, textAlign: 'left' }}>Field</th>
                <th style={{ padding: '5px 8px', fontSize: 11, textAlign: 'left' }}>On the create screen of</th>
                <th style={{ padding: '5px 8px', fontSize: 11, textAlign: 'right', whiteSpace: 'nowrap' }}>Holds a value on</th>
              </tr>
            </thead>
            <tbody>
              {grouped ? (
                <>
                  {groupRow('Important fields to consider clearing', groups.important, '— the admin’s short list, Page settings → Cloning')}
                  {groups.important.map(fieldRow)}
                  {groupRow('Every other field', groups.other, '')}
                  {groups.other.map(fieldRow)}
                </>
              ) : shownFields.map(fieldRow)}
              {q && !shownFields.length ? (
                <tr><td colSpan={5} style={{ ...S.tiny, padding: '10px 8px', textAlign: 'center' }}>No field matches &ldquo;{fieldQ.trim()}&rdquo;.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {/* THE GRIP. Drag it down for a taller table, up for a shorter one;
            double-click puts it back. */}
        <div role="separator" aria-orientation="horizontal" aria-label="Resize the fields table"
          title="Drag to make the fields table taller or shorter — double-click to reset"
          onMouseDown={(e) => { e.preventDefault(); fieldsDrag.current = { y0: e.clientY, h0: fieldsH }; document.body.style.cursor = 'ns-resize'; }}
          onDoubleClick={() => { setFieldsH(260); try { localStorage.removeItem('grids.clone.fieldsH'); } catch { /* storage may be blocked */ } }}
          style={{ height: 10, cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${ui.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', background: ui.surface }}>
          <span style={{ width: 36, height: 3, borderRadius: 3, background: ui.faint, opacity: 0.8 }} />
        </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${ui.border}`, paddingTop: 10 }}>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          Clone counter
          <input type="number" min="1" max="50" value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            style={{ ...S.input, width: 70, padding: '5px 8px' }} />
          cop{Number(copies) === 1 ? 'y' : 'ies'}
        </label>
        <span style={S.tiny}>{counted} work item(s) × {Math.max(1, Number(copies) || 1)} = <strong style={{ color: ui.fg }}>{total}</strong> to make{clear.size ? ` · ${clear.size} field(s) cleared` : ''}{editList.length ? ` · ${editList.length} field(s) changed` : ''}</span>
        {blocked && why ? <span style={{ ...S.tiny, color: ui.amber, fontWeight: 700 }}>{why}</span> : null}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          <button onClick={onClose} style={btn}>Cancel</button>
          <button onClick={start} disabled={blocked}
            style={{ ...S.pill(true), padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: blocked ? 0.5 : 1 }}>
            <DnaIcon size={16} title="" /> Clone
          </button>
        </span>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  const wide = editList.length > 1;
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Clone work items" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 4200, background: 'rgba(9, 15, 26, 0.55)', display: 'flex', alignItems: narrow ? 'flex-end' : 'center', justifyContent: 'center', padding: narrow ? 0 : 24, boxSizing: 'border-box', fontFamily: FONT }}>
      <style>{'@keyframes wip-sweep { 0% { left: -40% } 100% { left: 110% } }'}</style>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: ui.card, color: ui.fg, border: `1px solid ${ui.border}`, borderRadius: narrow ? '14px 14px 0 0' : 14,
        boxShadow: '0 24px 64px rgba(0,0,0,.35)', width: narrow ? '100%' : `min(${wide ? 1400 : 1100}px, 100%)`, maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', transition: 'width .15s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${ui.border}` }}>
          <CloneBanner height={46} title="Clone" />
          <div>
            <strong style={{ fontSize: 14 }}>Clone {roots.length === 1 ? roots[0] : `${roots.length} work items`}</strong>
            <div style={S.tiny}>{roots.length === 1 ? summaryOf(byKey.get(roots[0])) : roots.join(', ')}</div>
          </div>
          <button onClick={onClose} aria-label="Close" title="Close (Esc)" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: ui.muted, fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 4px', fontFamily: FONT }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '12px 14px 14px', flex: '1 1 auto' }}>{body}</div>
      </div>
    </div>,
    document.body,
  );
}

// THE PROJECT THE COPIES GO TO, FOUND BY TYPING. A native select over a
// site's hundreds of projects is a scroll through all of them; this narrows
// as the reader types — key or name, every word (rankProjects in
// ./clone.js) — with the arrow keys and Enter. Its list floats inside the
// dialog (the shared SearchableSelect's sits under a dialog this high), and
// Escape closes the list, not the dialog.
function ProjectPick({ value, projects, ownLabel, onChange, ui, S, changed }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const matches = useMemo(() => rankProjects(projects || [], q), [projects, q]);
  const shown = matches.slice(0, 80);
  // The first row puts the copies back in their own project.
  const rows = [{ key: '', label: ownLabel }, ...shown.map((p) => ({ key: p.key, name: p.name, label: `${p.key} — ${p.name}` }))];
  useEffect(() => {
    const el = listRef.current && listRef.current.children[hi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi]);
  const chosen = (projects || []).find((p) => p.key === value);
  const pick = (k) => { onChange(k); setOpen(false); setQ(''); };
  const loading = projects === null;
  return (
    <div ref={ref} style={{ position: 'relative', flex: '0 1 460px', minWidth: 280 }}>
      <input value={open ? q : (chosen ? `${chosen.key} — ${chosen.name}` : ownLabel)}
        placeholder={loading ? 'reading the projects…' : 'Type a project key or name…'}
        onFocus={(e) => { setOpen(true); setQ(''); setHi(0); e.currentTarget.select(); }}
        onChange={(e) => { setQ(e.target.value); setHi(0); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(rows.length - 1, h + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (open && rows[hi]) pick(rows[hi].key); }
          else if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); setQ(''); }
        }}
        aria-label="The project the copies are made in"
        style={{ ...S.input, width: '100%', padding: '5px 9px', fontSize: 12.5, borderColor: changed ? ui.primary : ui.border, fontWeight: changed && !open ? 700 : 400 }} />
      {open ? (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 3, zIndex: 30, background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 8, boxShadow: '0 10px 28px rgba(0,0,0,.22)', overflow: 'hidden' }}>
          <div ref={listRef} style={{ maxHeight: 260, overflowY: 'auto', padding: 4 }}>
            {rows.map((r, i) => (
              <div key={r.key || '(own)'} onMouseDown={(e) => { e.preventDefault(); pick(r.key); }} onMouseEnter={() => setHi(i)}
                style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5,
                  background: i === hi ? ui.surface : 'transparent', fontWeight: r.key === (value || '') ? 700 : 400, color: r.key ? ui.fg : ui.muted }}>
                {r.key ? <span style={{ color: ui.blue, fontWeight: 700, flex: '0 0 auto' }}>{r.key}</span> : null}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.key ? r.name : r.label}</span>
              </div>
            ))}
          </div>
          <div style={{ ...S.tiny, padding: '4px 10px', borderTop: `1px solid ${ui.border}` }}>
            {loading ? 'reading the projects…'
              : (q.trim() && !matches.length ? `No project matches “${q.trim()}”.`
                : `${matches.length.toLocaleString()} project${matches.length === 1 ? '' : 's'}${matches.length > shown.length ? ` — the first ${shown.length}; type more to narrow` : ''} · ↑↓ and Enter`)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// A value as words, for the placeholders: the label of an option, the name
// of a person, the day of a date, the words of a list.
function textOf(v) {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.map(textOf).filter(Boolean).join(', ');
  if (typeof v === 'object') return v.displayName || v.value || v.name || v.key || String(v.id || '');
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}T/.test(s) ? s.slice(0, 10) : s;
}
